import React, { createContext, useState, useContext, ReactNode, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import i18n, { saveLanguageToStorage, hasSavedLanguage } from '../i18n';
import LanguageSelectionScreen from '../components/LanguageSelectionScreen';

type LanguageContextType = {
  language: string;
  changeLanguage: (lang: string) => void;
  changeLanguageAsync: (lang: string) => Promise<void>;
  t: (key: string, options?: any) => string;
};

const LanguageContext = createContext<LanguageContextType>({
  language: 'es', // Default fallback
  changeLanguage: () => {},
  changeLanguageAsync: async () => {},
  t: (key: string) => key,
});

export const useLanguage = () => useContext(LanguageContext);

interface LanguageProviderProps {
  children: ReactNode;
}

export const LanguageProvider: React.FC<LanguageProviderProps> = ({ children }) => {
  const { t, i18n: i18nInstance } = useTranslation();
  const [language, setLanguage] = useState('es'); // Start with default
  const [showLanguageSelection, setShowLanguageSelection] = useState(false);
  const [isI18nReady, setIsI18nReady] = useState(false);

  // Initialize when i18n is ready
  useEffect(() => {
    const handleI18nReady = () => {
      console.log('i18n initialized, current language:', i18n.language);
      
      // Sync language state with i18n
      setLanguage(i18n.language);
      
      // Check if user has a saved language preference
      const hasLanguagePreference = hasSavedLanguage();
      console.log('User has saved language preference:', hasLanguagePreference);
      
      if (hasLanguagePreference) {
        console.log('Using saved language preference, hiding selection screen');
        setShowLanguageSelection(false);
      } else {
        console.log('No saved preference, showing language selection screen');
        setShowLanguageSelection(true);
      }
      
      setIsI18nReady(true);
    };
    
    if (i18n.isInitialized) {
      handleI18nReady();
    } else {
      i18n.on('initialized', handleI18nReady);
    }
    
    return () => {
      i18n.off('initialized', handleI18nReady);
    };
  }, []);

  // Sync language state with i18n changes
  useEffect(() => {
    const handleLanguageChange = (lng: string) => {
      console.log(`Language changed to: ${lng}`);
      setLanguage(lng);
    };

    i18n.on('languageChanged', handleLanguageChange);

    return () => {
      i18n.off('languageChanged', handleLanguageChange);
    };
  }, []);

  const changeLanguage = (lang: string) => {
    console.log('changeLanguage called with:', lang);
    
    // Save to storage first
    saveLanguageToStorage(lang);
    
    // Change i18n language (this will trigger the event listener above)
    i18n.changeLanguage(lang).then(() => {
      console.log('changeLanguage completed for:', lang);
     // Force page reload to ensure all components refresh with new language
     window.location.reload();
    }).catch(error => {
      console.error('Error in changeLanguage:', error);
     // Force page reload even on error to ensure consistent state
     window.location.reload();
    });
    
    // Dispatch custom event to notify other components
    window.dispatchEvent(new CustomEvent('language-changed', { detail: lang }));
  };

  const changeLanguageAsync = async (lang: string): Promise<void> => {
    console.log('changeLanguageAsync called with:', lang);
    
    // Save to storage first
    saveLanguageToStorage(lang);
    
    try {
      // Wait for i18n to complete the language change
      await i18n.changeLanguage(lang);
      console.log('changeLanguageAsync completed for:', lang);
      
      // Update local state to match i18n
      setLanguage(i18n.language);
      
      // Dispatch custom event to notify other components
      window.dispatchEvent(new CustomEvent('language-changed', { detail: lang }));
      
      // Small delay to ensure all components have updated
      await new Promise(resolve => setTimeout(resolve, 100));
    } catch (error) {
      console.error('Error in changeLanguageAsync:', error);
      throw error;
    }
  };

  const handleLanguageSelected = () => {
    console.log('Language selection completed, current language:', i18n.language);
    
    // Force update language state to match i18n
    setLanguage(i18n.language);
    
    // Hide language selection
    setShowLanguageSelection(false);
  };

  // Don't render anything until i18n is ready
  if (!isI18nReady) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-100">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-gray-600">Cargando...</p>
        </div>
      </div>
    );
  }

  // Show language selection screen if needed
  if (showLanguageSelection) {
    return <LanguageSelectionScreen onLanguageSelected={handleLanguageSelected} />;
  }

  return (
    <LanguageContext.Provider value={{ language, changeLanguage, changeLanguageAsync, t }}>
      {children}
    </LanguageContext.Provider>
  );
};

export default LanguageContext;